<div class="container-fluid">
  <div routerLink="/proctoring-dashboard/proto-configs">
    <i class="bi bi-arrow-left back-icon"></i> Back
  </div>
  <h1>New proctoring configuration</h1>

  <p class="subtitle">Default configuration for the organization.</p>

  <form [formGroup]="configForm">
    <div class="card">
      <h2>Configuration details</h2>

      <p>Give this configuration a name and describe what it is used for.</p>

      <div class="form-row">
        <div class="field">
          <label> Configuration name <span>*</span> </label>

          <input
            placeholder="e.g. High-stakes examination"
            formControlName="name"
          />
             <small
                  class="text-danger smaller-text"
                  *ngIf="configForm.get('name')?.touched && configForm.get('name')?.errors?.['required']">
                  Configuration name is required.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configForm.get('name')?.touched && configForm.get('name')?.errors?.['minLength']">
                  Configuration name must contain at least 3 characters.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configForm.get('name')?.touched && configForm.get('name')?.errors?.['maxLength']">
                  Configuration name cannot exceed 100 characters.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configForm.get('name')?.touched && configForm.get('name')?.errors?.['trimSpaces']">
                  Leading and trailing spaces are not allowed.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configForm.get('name')?.touched && configForm.get('name')?.errors?.['whitespace']">
                  Configuration name cannot be empty.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configForm.get('name')?.touched && configForm.get('name')?.errors?.['invalidCharacters']">
                  Invalid characters are not allowed.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configForm.get('name')?.touched && configForm.get('name')?.errors?.['firstCharacter']">
                  Configuration name cannot start with a special character.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configForm.get('name')?.touched && configForm.get('name')?.errors?.['duplicate']">
                  Configuration name already exists.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configForm.get('name')?.touched && configForm.get('name')?.errors?.['html']">
                  Invalid input detected.
                </small>
        </div>

        <div class="field">
          <label> Description </label>

          <textarea
            formControlName="description"
            placeholder="Short description of when to use this configuration"
          >
          </textarea>
          <small
            class="smaller-text text-danger"
            *ngIf="configForm.get('description')?.touched && configForm.get('description')?.errors?.['maxLength']">
            Description cannot exceed 500 characters.
          </small>

          <small
            class="smaller-text text-danger"
            *ngIf="configForm.get('description')?.touched && configForm.get('description')?.errors?.['trimSpaces']">
            Extra spaces will be removed.
          </small>

          <small
            class="smaller-text text-danger"
            *ngIf="configForm.get('description')?.touched && configForm.get('description')?.errors?.['whitespace']">
            Please enter a valid description.
          </small>

          <small
            class="smaller-text text-danger"
            *ngIf="configForm.get('description')?.touched && configForm.get('description')?.errors?.['invalidCharacters']">
            Invalid characters entered.
          </small>

          <small
            class="smaller-text text-danger"
            *ngIf="configForm.get('description')?.touched && configForm.get('description')?.errors?.['html']">
            Invalid input detected.
          </small>
        </div>
      </div>

      <div class="default-box">
        <div>
          <div class="default-title">
            <b>Set as default</b>

            <span
              class="default-badge"
              *ngIf="configForm.get('isDefault')?.value"
            >
              Default
            </span>
          </div>

          <p>
            Use this configuration as the organization default for any
            assessment that doesn't specify one.
          </p>
        </div>

        <div
          class="switch"
          [class.active]="configForm.get('isDefault')?.value"
          (click)="openDefaultsDialogBox()">
          <span></span>
        </div>
      </div> 
    </div>

    <div class="card">
      <h2>Browser & device restrictions</h2>

      <p>Control which browsers and devices candidates may use.</p>

      <div class="browser-device">
        <div>
          <label>Allowed browsers<span>*</span></label>

          <div class="chips">
            <button
              type="button"
              *ngFor="let browser of browsers"
              [class.active]="
                configForm.value.allowedBrowsers.includes(browser.name)
              "
              (click)="toggleBrowser(browser.name)"
            >
              <i
                *ngIf="configForm.value.allowedBrowsers.includes(browser.name)"
                class="bi bi-check"
              >
              </i>
              {{ browser.name }}
            </button>
          </div>
          <small
            class="smaller-text text-danger"
            *ngIf="configForm.get('allowedBrowsers')?.touched && configForm.get('allowedBrowsers')?.hasError('required')
          ">
            Please select at least one browser.
           </small>
        </div>

        <div>
          <label>Allowed devices<span>*</span></label>

          <div class="chips">
            <button
              type="button"
              *ngFor="let device of devices"
              [class.active]="
                configForm.value.allowedDevices.includes(device.name)
              "
              (click)="toggleDevice(device.name)"
            >
              <i
                *ngIf="configForm.value.allowedDevices.includes(device.name)"
                class="bi bi-check"
              >
              </i>
              {{ device.name }}
            </button>
          </div>
            <small
            class="smaller-text text-danger"
            *ngIf="configForm.get('allowedDevices')?.touched && configForm.get('allowedDevices')?.hasError('required')
            ">
            Please select at least one device.
        </small>
        </div>
      </div>
    </div>

    <div class="card">
      <h2>Capture requirements</h2>

      <p>Choose what candidates must share, and what is recorded for review.</p>

      <div class="capture-grid">
        <div class="capture-box">
          <h3><i class="bi bi-camera-fill"></i> Camera</h3>

          <div class="toggle-row">
            <div class="label-with-info">
              <span> Require camera </span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Candidate must grant camera permission before starting the
                  assessment.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.on]="configForm.get('requireCamera')?.value"
              (click)="
                configForm.patchValue({
                  requireCamera: !configForm.get('requireCamera')?.value,
                })
              "
            >
              <span></span>
            </div>
          </div>

          <div
            class="child-toggle"
            [class.disabled]="!configForm.get('requireCamera')?.value"
          >
            <div class="label-with-info">
              <span> Record camera</span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Saves camera footage for evidence review.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.on]="configForm.get('recordCamera')?.value"
              (click)="
                configForm.patchValue({
                  recordCamera: !configForm.get('recordCamera')?.value,
                })
              "
            >
              <span></span>
            </div>
          </div>
        </div>

        <div class="capture-box">
          <h3><i class="bi bi-mic-fill"></i> Microphone</h3>

          <div class="toggle-row">
            <div class="label-with-info">
              <span> Require microphone</span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Enables detection of background audio and voices.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.on]="configForm.get('requireMicrophone')?.value"
              (click)="
                configForm.patchValue({
                  requireMicrophone:
                    !configForm.get('requireMicrophone')?.value,
                })
              "
            >
              <span></span>
            </div>
          </div>

          <div
            class="child-toggle"
            [class.disabled]="!configForm.get('requireMicrophone')?.value"
          >
            <div class="label-with-info">
              <span> Record audio </span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Records candidate audio for later review.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.on]="configForm.get('recordAudio')?.value"
              (click)="
                configForm.patchValue({
                  recordAudio: !configForm.get('recordAudio')?.value,
                })
              "
            >
              <span></span>
            </div>
          </div>
        </div>

        <div class="capture-box">
          <h3><i class="bi bi-display-fill"></i> Screen</h3>

          <div class="toggle-row">
            <div class="label-with-info">
              <span>Require screen sharing</span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Candidates must share their entire screen during the
                  assessment.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.on]="configForm.get('requireScreenShare')?.value"
              (click)="
                configForm.patchValue({
                  requireScreenShare:
                    !configForm.get('requireScreenShare')?.value,
                })
              "
            >
              <span></span>
            </div>
          </div>

          <div
            class="child-toggle"
            [class.disabled]="!configForm.get('requireScreenShare')?.value"
          >
            <div class="label-with-info">
              <span>Record screen</span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Stores screen recording for later review.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.on]="configForm.get('recordScreen')?.value"
              (click)="
                configForm.patchValue({
                  recordScreen: !configForm.get('recordScreen')?.value,
                })
              "
            >
              <span></span>
            </div>
          </div>

          <!-- <div
            class="toggle-row child-toggle"
            [class.on]="configForm.get('recordScreen')?.value"
          >
            <div class="label-with-info">
              <span>Screen monitoring</span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Monitor tab switches, window changes, and full-screen exits.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.on]="configForm.get('screenMonitoring')?.value"
              (click)="
                configForm.patchValue({
                  screenMonitoring: !configForm.get('screenMonitoring')?.value,
                })
              "
            >
              <span></span>
            </div>
          </div>

          <div
            class="toggle-row child-toggle"
            [class.disabled]="!configForm.get('recordScreen')?.value"
          >
            <div class="label-with-info">
              <span>Screenshot capture</span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Periodic screenshots at moments of violation.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.on]="configForm.get('screenshotCapture')?.value"
              (click)="
                configForm.patchValue({
                  screenshotCapture:
                    !configForm.get('screenshotCapture')?.value,
                })
              "
            >
              <span></span>
            </div>
          </div> -->

          <div class="toggle-row">
            <div class="label-with-info">
              <span>Require fullscreen</span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Prevents candidates from switching to other applications.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.on]="configForm.get('requireFullscreen')?.value"
              (click)="
                configForm.patchValue({
                  requireFullscreen:
                    !configForm.get('requireFullscreen')?.value,
                })
              "
            >
              <span></span>
            </div>
          </div>
        </div>
      </div>

    <!-- <div class="violation-card mt-4">
      <div class="section-header">
        <h3>Violation controls</h3>
        <p>Choose how proctoring violations are processed during an assessment.</p>
      </div>

      <div class="setting-card">
        <div class="setting-content">
          <div class="setting-title">
            <h4>
              Enable Violation Engine
              <i class="bi bi-info-circle"></i>
            </h4>

            <p>
              Detect and record proctoring violations during the assessment.
            </p>

            <span class="status-text">
              <strong>Enabled:</strong> Server accepts incoming violations.
              Violation analysis runs. All configured proctoring rules are applied.
            </span>
          </div>

          <mat-slide-toggle
            color="primary"
            [(ngModel)]="violationEngine">
          </mat-slide-toggle>
        </div>
      </div>

      <div class="setting-card">
        <div class="setting-content">
          <div class="setting-title">
            <h4>
              Auto Terminate on Maximum Violations
              <i class="bi bi-info-circle"></i>
            </h4>

            <p>
              Automatically end the assessment when configured violation limits are
              exceeded.
            </p>

            <span class="status-text">
              <strong>Enabled:</strong> Candidate is automatically terminated when
              any configured threshold is exceeded.
            </span>
          </div>

          <mat-slide-toggle
            color="primary"
            [(ngModel)]="autoTerminate">
          </mat-slide-toggle>
        </div>
      </div>
    </div> -->

      <div class="card">
        <h2>Global escalation thresholds</h2>
        <p>
          Configure the maximum number of violations allowed for each severity
          before the configured escalation action is triggered. These thresholds
          apply across the entire assessment session.
        </p>

        <div class="threshold-grid">
          <div class="threshold-box medium">
            <h3>
              <span class="dot medium-dot"></span>
              Medium
            </h3>

            <label>Maximum violations<span>*</span></label>
            <input type="number" min="0" formControlName="maxMedium" />
            <small
            class="text-danger smaller-text"
            *ngIf="configForm.get('maxMedium')?.touched && configForm.get('maxMedium')?.errors?.['numbersOnly']">
            Only numbers are allowed.
          </small>

          <small
            class="text-danger smaller-text"
            *ngIf="configForm.get('maxMedium')?.touched && configForm.get('maxMedium')?.errors?.['min']">
            Value cannot be less than 0.
          </small>

          <small
            class="text-danger smaller-text"
            *ngIf="configForm.get('maxMedium')?.touched && configForm.get('maxMedium')?.errors?.['max']">
            Value cannot be greater than 100.
          </small>
          <small
          class="text-danger smaller-text"
          *ngIf="configForm.get('maxMedium')?.errors?.['zero']">
            Value cannot be 0.
          </small>

          <small
               class="text-danger smaller-text"
               *ngIf="configForm.get('maxMedium')?.touched && configForm.get('maxMedium')?.errors?.['required']">
                Maximum violations is required.
          </small>
            <small>Minor suspicious activities</small>
          </div>

          <div class="threshold-box high">
            <h3>
              <span class="dot high-dot"></span>
              High
            </h3>

            <label>Maximum violations<span>*</span></label>
            <input type="number" min="0" formControlName="maxHigh" />
              <small
                class="text-danger smaller-text"
                *ngIf="configForm.get('maxHigh')?.touched && configForm.get('maxHigh')?.errors?.['numbersOnly']">
                Only numbers are allowed.
              </small>

              <small
                class="text-danger smaller-text"
                *ngIf="configForm.get('maxHigh')?.touched && configForm.get('maxHigh')?.errors?.['min']">
                Value cannot be less than 0.
              </small>

              <small
                class="text-danger smaller-text"
                *ngIf="configForm.get('maxHigh')?.touched && configForm.get('maxHigh')?.errors?.['max']">
                Value cannot be greater than 100.
              </small>
              <small
                  class="text-danger smaller-text"
                  *ngIf="configForm.get('maxHigh')?.errors?.['zero']">
              Value cannot be 0.
            </small>
                      <small
            class="text-danger smaller-text"
            *ngIf="configForm.get('maxHigh')?.touched && configForm.get('maxHigh')?.errors?.['required']">
            Maximum violations is required.
              </small>
            <small>Serious suspicious activities</small>
          </div>

          <div class="threshold-box critical">
            <h3>
              <span class="dot critical-dot"></span>
              Critical
            </h3>

            <label>Maximum violations<span>*</span></label>
            <input type="number" min="0" formControlName="maxCritical" />
            <small
              class="text-danger smaller-text"
              *ngIf="configForm.get('maxCritical')?.touched && configForm.get('maxCritical')?.errors?.['numbersOnly']">
              Only numbers are allowed.
            </small>

            <small
              class="text-danger smaller-text"
              *ngIf="configForm.get('maxCritical')?.touched && configForm.get('maxCritical')?.errors?.['min']">
              Value cannot be less than 0.
            </small>

            <small
              class="text-danger smaller-text"
              *ngIf="configForm.get('maxCritical')?.touched && configForm.get('maxCritical')?.errors?.['max']">
              Value cannot be greater than 100.
            </small>
            <small
              class="text-danger smaller-text"
             *ngIf="configForm.get('maxCritical')?.errors?.['zero']">
            Value cannot be 0.
          </small>

                    <small
            class="text-danger smaller-text"
            *ngIf="configForm.get('maxCritical')?.touched && configForm.get('maxCritical')?.errors?.['required']">
            Maximum violations is required.
            </small>
            <small>Severe violations</small>
          </div>
        </div>
      </div>

      <div class="footer-buttons">
        <button class="cancel" type="button">Cancel</button>
        <button
          class="save"
          type="button"
          (click)="submitConfiguration()"
          [disabled]="configForm.invalid || isLoading"
          *hasPermission="PermissionForStudents.PROCTORING_CREATE"
        >
          <ng-container *ngIf="isLoading; else noLoading">
            <span
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
            <span> Saving...</span>
          </ng-container>
          <ng-template #noLoading>
            <span> Save configuration </span>
          </ng-template>
        </button>
      </div>
    </div>
  </form>
</div>